<template>
  <template v-if="deal.packages.length > 0">
    <FormSeparator
      ><template #icon>
        <IconColoredDeal />
      </template>
      {{ capitalize($t('names.deal', { n: 2 })) }}</FormSeparator
    >
    <div class="row mb-5">
      <div class="col-12">
        <div class="deals-grid">
          <div
            v-for="childDeal in deal.packages"
            :key="childDeal.id"
            class="deal">
            <GreenScore
              class="green-score"
              :is-animating="true"
              :show-green-leaf="childDeal.co2EmissionsScore >= DEFAULT_SUSTAINABILITY_THRESHOLD"
              :co2-score="childDeal.co2EmissionsScore"
              width="5rem"
              height="5rem" />
            <div class="px-4 pt-4 deal-header">
              <div class="title">
                {{ childDeal.name }}
              </div>
              <div class="badge-container">
                <span class="badge badge-sm badge-disabled-hollow monospace font-weight-normal">
                  {{ childDeal.dealID }}
                </span>
              </div>
            </div>
            <form-separator
              :icon="markRaw(IconColoredDelivery)"
              margin-size="xs"
              >{{ capitalize($t('names.kpi', { n: 2 })) }}</form-separator
            >
            <div class="deal-kpis m-4">
              <KPI
                value-style="font-size: '1.5em'"
                :value="formatNumber(estimatedDeliveryStats?.byPackage[childDeal.id]?.dailyImpressions, '0.0a') ?? DASH"
                :label="$t('messages.dailyImpressions', { n: 2 })"
                :icon="markRaw(IconColoredImpression)"></KPI>
              <KPI
                value-style="font-size: '1.5em'"
                :value="
                  formatCurrency(estimatedDeliveryStats?.byPackage[childDeal.id]?.eCPM || 0, {
                    currencyCode: deal.currencyInformation.currency,
                    showCurrencyCode: true,
                  }) ?? DASH
                "
                :label="$t('names.cpm', { n: 1 })"
                :icon="markRaw(IconColoredCPM)"></KPI>
              <KPI
                value-style="font-size: '1.5em'"
                :value="formatNumber(estimatedDeliveryStats?.byPackage[childDeal.id]?.unitCount, '0,0a') ?? DASH"
                :label="$t('names.screen', { n: 2 })"
                :icon="markRaw(IconColoredScreen)"></KPI>
            </div>
            <form-separator
              :icon="markRaw(IconColoredMediaOwner)"
              margin-size="xs"
              >{{ capitalize($t('names.mediaOwner', { n: 2 })) }}</form-separator
            >
            <div class="media-owners mt-4 mb-4">
              <img
                v-if="mediaOwners.find((x) => x.id === childDeal.organizationID)?.logoUrl"
                :src="mediaOwners.find((x) => x.id === childDeal.organizationID)?.logoUrl"
                :alt="mediaOwners.find((x) => x.id === childDeal.organizationID)?.name" />
              <span v-else>{{
                mediaOwners.find((x) => x.id === childDeal.organizationID)?.name || capitalize($t('names.unknown'))
              }}</span>
            </div>

            <div class="child-deal-tile__actions">
              <router-link
                :to="childDeal.endpoint"
                class="btn btn-secondary-hollow btn-xs">
                {{ $t('messages.seeDetails') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
  import { capitalize, markRaw, onMounted } from 'vue';

  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import {
    IconColoredCPM,
    IconColoredDeal,
    IconColoredDelivery,
    IconColoredImpression,
    IconColoredMediaOwner,
    IconColoredScreen,
  } from '@/common/icons/colored';
  import CuratedDeliveryStatistics from '@/common/models/deliveryStatistics/CuratedDeliveryStatistics';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import Package, { DEFAULT_SUSTAINABILITY_THRESHOLD } from '@/common/models/package/Package';
  import { DASH, formatCurrency, formatNumber } from '@/globals';
  import KPI from '@/org-app/components/controls/KPI.vue';

  interface IDealDetailsChildDealsProps {
    deal: Package;
    estimatedDeliveryStats: CuratedDeliveryStatistics;
    mediaOwners: MediaOwner[];
  }

  const props = defineProps<IDealDetailsChildDealsProps>();

  onMounted(() => {});
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
  .deals-grid {
    display: grid;
    gap: 2.4rem;
    grid-template-columns: 49% 49%;
  }

  .deal {
    box-shadow: $light-shadow;
    border-radius: 1.6rem;
    background-color: #fff;
    position: relative;

    .green-score {
      position: absolute;
      width: 5rem;
      height: 5rem;
      top: -1.5rem;
      left: -1.5rem;
    }

    .deal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title {
        font-weight: bold;
        font-size: 1.2em;
        margin-left: 2.4rem;
      }
      .badge-container {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        .badge {
          margin-left: auto;
        }
      }
    }

    .deal-kpis {
      display: grid;
      grid-template-columns: 33% 33% 33%;
    }
  }

  .media-owners {
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-height: 3rem;
    }
  }

  .child-deal-tile__actions {
    display: flex;
    justify-content: flex-end;
    margin: 0 1.6rem 1.6rem 0;
  }
</style>
